/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react';
import type { QuestionnaireTemplate } from '../types/types';
import { supabase } from '../lib/supabase';
import { captureSupabaseError } from '../lib/sentry';

export const useQuestionnaireData = (templateId: string) => {
  const [template, setTemplate] = useState<QuestionnaireTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTemplate = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('questionnaire_templates')
        .select(
          `
    *,
    sections:question_sections(
      *,
      questions:questions(*)
    )
  `
        )
        .eq('id', templateId)
        .eq('sections.questions.deleted', false)
        .single();

      if (error) {
        setError(error.message);
      } else if (data) {
        const mappedTemplate: QuestionnaireTemplate = {
          id: data.id,
          key: data.key,
          title: data.title,
          description: data.description,
          status: data.status,
          version: data.version,
          created_by: data.created_by,
          created_at: data.created_at,
          updated_at: data.updated_at,
          metadata: data.metadata ?? null,
          organisation_id: data.organisation_id ?? null,
          is_default: data.is_default ?? false,
          sections: (data.sections || [])
            .filter((s: any) => !s.deleted)
            .map((s: any, i: number) => ({
              id: s.id,
              key: s.key,
              title: s.title?.en ?? '',
              ordinal: s.ordinal ?? i,
              description: s.description?.en ?? '',
              exclude_from_score: s.exclude_from_score ?? false,
              questions: (s.questions || []).map((q: any, j: number) => ({
                id: q.id,
                key: q.key,
                ordinal: q.ordinal ?? j,
                type: q.type,
                label: q.label?.en ?? '',
                help_text: q.help_text?.en ?? '',
                options: q.options ?? [],
                required: q.required,
                hidden: q.hidden,
                section_id: s.id,
                condition: q.condition ?? undefined,
                group_id: q.group_id ?? null,
                include_in_report: q.include_in_report ?? false,
              })),
            })),
        };
        setTemplate(mappedTemplate);
      }
      setLoading(false);
    };

    if (templateId) fetchTemplate();
  }, [templateId]);

  const saveTemplate = async (updatedTemplate: QuestionnaireTemplate) => {
    if (!updatedTemplate) {
      return { error: new Error('No template to save') };
    }

    if (updatedTemplate.sections === undefined) {
      return { error: new Error('Template sections are undefined') };
    }

    setSaving(true);
    setError(null);

    try {
      const { data: userData, error: userError } =
        await supabase.auth.getUser();
      if (userError || !userData?.user)
        throw userError || new Error('User not authenticated');

      // 1️⃣ Upsert the template (1 request)
      const { error: templateError } = await supabase
        .from('questionnaire_templates')
        .upsert({
          id: updatedTemplate.id,
          title: updatedTemplate.title,
          description: updatedTemplate.description,
          status: updatedTemplate.status,
          version: updatedTemplate.version,
          organisation_id: updatedTemplate.organisation_id,
        });

      if (templateError) throw templateError;

      // 2️⃣ Prepare bulk arrays
      const sectionsPayload = updatedTemplate.sections.map((section) => ({
        id: section.id,
        template_id: updatedTemplate.id,
        key: section.key,
        title: { en: section.title },
        description: { en: section.description },
        ordinal: section.ordinal,
        exclude_from_score: section.exclude_from_score,
      }));

      const questionsPayload = updatedTemplate.sections.flatMap((section) =>
        section.questions.map((question) => ({
          id: question.id,
          section_id: section.id,
          template_id: updatedTemplate.id,
          key: question.key,
          ordinal: question.ordinal,
          type: question.type,
          label: { en: question.label },
          help_text: question.help_text ? { en: question.help_text } : {},
          options: question.options ?? [],
          required: question.required,
          hidden: question.hidden,
          condition: question.condition ?? null,
          group_id: question.group_id ?? null,
          include_in_report: question.include_in_report ?? false,
        }))
      );

      // 3️⃣ Bulk upsert sections (1 request)
      if (sectionsPayload.length > 0) {
        const { error: sectionError } = await supabase
          .from('question_sections')
          .upsert(sectionsPayload);

        if (sectionError) throw sectionError;
      }

      // 4️⃣ Bulk upsert questions (1 request)
      if (questionsPayload.length > 0) {
        const { error: questionError } = await supabase
          .from('questions')
          .upsert(questionsPayload);

        if (questionError) throw questionError;
      }

      setSaving(false);
      return { error: null };
    } catch (err: any) {
      captureSupabaseError(err, { op: 'questionnaire.save' });
      setSaving(false);
      setError(err.message || 'An error occurred while saving');
      return { error: err };
    }
  };

  return { template, setTemplate, loading, saving, error, saveTemplate };
};
